/**
 * Aperçu d'un espace consultant de démonstration (jeton Bearer read-only).
 *
 * Quand un admin prévisualise un consultant fictif, le BE délivre un jeton court
 * (30 min, lecture seule, sample-only). On le stocke ici et `httpClient` l'envoie
 * en `Authorization: Bearer` **à la place** du token admin, pour toute requête
 * sécurisée — le BE renvoie alors les données du consultant fictif et refuse
 * toute écriture (403). Hors aperçu, ce scope n'est jamais défini.
 *
 * Même forme que `orgScope` (impersonation) : store module-level (car `httpClient`
 * est une fonction, pas un hook), framework-agnostique, `sessionStorage` propre à
 * l'onglet (l'aperçu ne fuit pas dans les autres onglets et disparaît à la
 * fermeture). Un mécanisme d'abonnement permet aux composants de réagir.
 */
const PREVIEW_SCOPE_KEY = 'demo:previewScope';

export type PreviewScope = {
  /** Jeton Bearer d'aperçu (éphémère, lecture seule). */
  token: string;
  /** Nom du consultant fictif prévisualisé (affiché dans le bandeau). */
  name: string;
};

const listeners = new Set<() => void>();

function readRaw(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(PREVIEW_SCOPE_KEY);
}

export function parsePreviewScope(raw: string | null): PreviewScope | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    if (v && typeof v === 'object' && v.token) {
      return { token: String(v.token), name: String(v.name ?? '') };
    }
  } catch {
    return null;
  }
  return null;
}

export const previewScope = {
  /** Snapshot brut (chaîne stable) — pour `useSyncExternalStore`. */
  getRaw: readRaw,
  /** Aperçu actif (jeton + nom), ou `null`. */
  get(): PreviewScope | null {
    return parsePreviewScope(readRaw());
  },
  /** Jeton d'aperçu actif — lu par `httpClient`. `null` hors aperçu. */
  getToken(): string | null {
    return parsePreviewScope(readRaw())?.token ?? null;
  },
  /** Fixe (ou nettoie) l'aperçu actif et notifie les abonnés. */
  set(scope: PreviewScope | null) {
    if (typeof window === 'undefined') return;
    if (scope?.token) {
      sessionStorage.setItem(
        PREVIEW_SCOPE_KEY,
        JSON.stringify({ token: scope.token, name: scope.name ?? '' }),
      );
    } else {
      sessionStorage.removeItem(PREVIEW_SCOPE_KEY);
    }
    listeners.forEach(l => l());
  },
  /** Abonnement (même onglet via `listeners`, autres onglets via `storage`). */
  subscribe(cb: () => void): () => void {
    listeners.add(cb);
    if (typeof window !== 'undefined') window.addEventListener('storage', cb);
    return () => {
      listeners.delete(cb);
      if (typeof window !== 'undefined') window.removeEventListener('storage', cb);
    };
  },
};
